import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@asm/ui";
import { adminApi, ReconCampaign, CreateCampaignRequest, UpdateCampaignRequest } from "@/lib/api";

const SECTORS = [
  { value: "unspecified", label: "Unspecified (any sector)" },
  { value: "banking", label: "Banking" },
  { value: "healthcare", label: "Healthcare" },
  { value: "government", label: "Government" },
  { value: "education", label: "Education" },
  { value: "technology", label: "Technology" },
  { value: "telecommunications", label: "Telecommunications" },
  { value: "energy", label: "Energy" },
  { value: "retail", label: "Retail" },
  { value: "insurance", label: "Insurance" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "media", label: "Media" },
  { value: "transportation", label: "Transportation" },
  { value: "other", label: "Other" },
];

const COUNTRIES = [
  { code: "XX", name: "Unspecified (multiple/any)" },
  { code: "CO", name: "Colombia" },
  { code: "CL", name: "Chile" },
  { code: "MX", name: "Mexico" },
  { code: "PE", name: "Peru" },
  { code: "AR", name: "Argentina" },
  { code: "BR", name: "Brazil" },
  { code: "EC", name: "Ecuador" },
  { code: "PA", name: "Panama" },
  { code: "CR", name: "Costa Rica" },
  { code: "DO", name: "Dominican Republic" },
  { code: "GT", name: "Guatemala" },
  { code: "HN", name: "Honduras" },
  { code: "SV", name: "El Salvador" },
  { code: "NI", name: "Nicaragua" },
  { code: "UY", name: "Uruguay" },
  { code: "PY", name: "Paraguay" },
  { code: "BO", name: "Bolivia" },
  { code: "VE", name: "Venezuela" },
  { code: "US", name: "United States" },
  { code: "ES", name: "Spain" },
  { code: "GB", name: "United Kingdom" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
];

interface CampaignFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign?: ReconCampaign;
  onSuccess: () => void;
}

export function CampaignForm({ open, onOpenChange, campaign, onSuccess }: CampaignFormProps) {
  const { toast } = useToast();
  const isEditing = !!campaign;

  const [name, setName] = useState(campaign?.name || "");
  const [description, setDescription] = useState(campaign?.description || "");
  const [sector, setSector] = useState(campaign?.sector || "");
  const [countryCode, setCountryCode] = useState(campaign?.country_code || "");
  const [priority, setPriority] = useState(campaign?.priority?.toString() || "0");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !sector || !countryCode) {
      toast({
        title: "Validation Error",
        description: "Name, sector, and country are required.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      if (isEditing && campaign) {
        const updates: UpdateCampaignRequest = {
          name: name.trim(),
          description: description.trim() || undefined,
          sector,
          country_code: countryCode,
          priority: parseInt(priority) || 0,
        };
        await adminApi.updateCampaign(campaign.id, updates);
        toast({ title: "Campaign Updated", description: `${name} has been updated.` });
      } else {
        const data: CreateCampaignRequest = {
          name: name.trim(),
          description: description.trim() || undefined,
          sector,
          country_code: countryCode,
          priority: parseInt(priority) || 0,
        };
        await adminApi.createCampaign(data);
        toast({ title: "Campaign Created", description: `${name} has been created.` });
      }
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to save campaign",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Campaign" : "New Campaign"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update campaign targeting and configuration."
              : "Create a campaign to group domains by sector and country for targeted analysis."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="e.g., Banking in Colombia"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              placeholder="Optional description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Sector</Label>
              <Select value={sector} onValueChange={setSector}>
                <SelectTrigger>
                  <SelectValue placeholder="Select sector" />
                </SelectTrigger>
                <SelectContent>
                  {SECTORS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Country</Label>
              <Select value={countryCode} onValueChange={setCountryCode}>
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <Input
              id="priority"
              type="number"
              placeholder="0"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Higher priority campaigns are scanned first in the round-robin schedule.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : isEditing ? "Save Changes" : "Create Campaign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
